import { z } from 'zod';
/**
 * JOD amounts (debts.md section 3, owner decision D14).
 *
 * Amounts cross the wire as decimal STRINGS and are stored as
 * `Decimal(15,5)`: at most 10 integer digits and five decimal places (owner
 * decision D14).
 * An amount with more significant decimals is refused, never rounded. These
 * helpers work on the string form only, so the web form and the API apply
 * exactly the same rule without passing through a JavaScript float.
 */
export const AMOUNT_DECIMALS = 5;
export const AMOUNT_MAX_INTEGER_DIGITS = 10;
export const CURRENCY = 'JOD';
const INPUT_PATTERN = /^\d+(\.\d+)?$/;
/** Number of significant fraction digits (trailing zeros carry no value). */
export function significantDecimals(amount) {
    const [, fraction = ''] = amount.trim().split('.');
    return fraction.replace(/0+$/, '').length;
}
/** True when the decimal string is strictly greater than zero. */
export function isPositiveAmount(amount) {
    const value = amount.trim();
    return !value.startsWith('-') && /[1-9]/.test(value);
}
function integerDigits(amount) {
    const [integer = ''] = amount.trim().split('.');
    return integer.replace(/^0+(?=\d)/, '').length;
}
/**
 * Canonical five-decimal form: "7.5" becomes "7.50000", "0010" becomes
 * "10.00000". Throws when the value has more than five significant decimals;
 * it never rounds.
 */
export function formatAmount(amount) {
    const value = amount.trim();
    if (significantDecimals(value) > AMOUNT_DECIMALS) {
        throw new RangeError('amount has more than five significant decimals');
    }
    const negative = value.startsWith('-');
    const [rawInteger = '0', rawFraction = ''] = (negative ? value.slice(1) : value).split('.');
    const integer = rawInteger.replace(/^0+(?=\d)/, '') || '0';
    const fraction = rawFraction.replace(/0+$/, '').padEnd(AMOUNT_DECIMALS, '0');
    return `${negative ? '-' : ''}${integer}.${fraction}`;
}
/**
 * A positive JOD amount typed by an employee: digits, an optional dot, at most
 * five significant decimals, at most 10 integer digits. Output is canonical
 * (five decimals).
 */
export const amountInputSchema = z
    .string()
    .trim()
    .regex(INPUT_PATTERN, 'amount must be a decimal number such as 150.25000')
    .refine((v) => significantDecimals(v) <= AMOUNT_DECIMALS, {
    message: 'amount must not have more than five decimals',
})
    .refine((v) => integerDigits(v) <= AMOUNT_MAX_INTEGER_DIGITS, { message: 'amount is too large' })
    .refine((v) => isPositiveAmount(v), { message: 'amount must be greater than zero' })
    .transform((v) => formatAmount(v));
/** Like {@link amountInputSchema} but zero is allowed (opening balances, commissions). */
export const nonNegativeAmountInputSchema = z
    .string()
    .trim()
    .regex(INPUT_PATTERN, 'amount must be a decimal number such as 150.25000')
    .refine((v) => significantDecimals(v) <= AMOUNT_DECIMALS, {
    message: 'amount must not have more than five decimals',
})
    .refine((v) => integerDigits(v) <= AMOUNT_MAX_INTEGER_DIGITS, { message: 'amount is too large' })
    .transform((v) => formatAmount(v));
/** An amount returned by the API: a decimal string with exactly five decimals, maybe signed. */
export const amountSchema = z.string().regex(/^-?\d+\.\d{5}$/, 'must be a five-decimal string');
//# sourceMappingURL=amount.js.map